/**
 * Business logic per Orders By Allowed Worker autocomplete
 * Logica complessa per autorizzazioni worker
 * Pura - senza dipendenze da Vue
 */

import { isArray } from 'lodash-es';
import ordersService from '../services/ordersService';
import type { JobOrderAutocompleteResult } from '../models/jobOrder';
import type { ODataQueryBuilder } from './types';
import type { ODataContext } from '~/mixins/filterHandler/types';

export interface OrdersByAllowedWorkerOptions {
	searchString?: string;
	id?: number | string | null;
	notInIds?: Array<number | string> | null;
	workerId?: number | string | null;
	date?: Date;
	loggedWorkerId?: number | string | null;
	isPureManager?: boolean;
}

export async function getOrdersByAllowedWorkerAutocomplete(
	buildQuery: ODataQueryBuilder,
	options: OrdersByAllowedWorkerOptions = {},
): Promise<JobOrderAutocompleteResult[]> {
	const {
		searchString = '',
		id = null,
		notInIds = null,
		workerId = null,
		date = new Date(),
		loggedWorkerId = null,
		isPureManager = false,
	} = options;

	const context: ODataContext = {
		select: 'id,code,name,isRequirementRequired',
		count: true,
		expand: {
			customer: {
				select: 'id,code,name',
			},
		},
		top: 20,
		orderBy: ['startDate desc', 'name'],
		filter: {
			and: [
				// Filtro per worker autorizzati (allowedWorkers OR allowedGroupWorkers)
				{
					or: [
						{
							allowedWorkers: {
								any: {
									workerId,
								},
							},
						},
						{
							allowedGroupWorkers: {
								any: {
									workerId,
								},
							},
						},
					],
				},
				// Filtro per ordini attivi (endDate null OR >= date)
				{
					or: [{ endDate: null }, { endDate: { ge: date } }],
				},
				{ startDate: { le: date } },
			],
		},
	};

	// Logica manager: se il worker loggato è diverso dal worker target E è pure manager
	if (workerId && loggedWorkerId && workerId !== loggedWorkerId && isPureManager) {
		context.filter!.and!.push({
			managers: {
				any: [{ workerId: loggedWorkerId }],
			},
		});
	}

	if (searchString !== '') {
		context.filter!.and!.push({
			or: [
				{ 'tolower(code)': { contains: searchString } },
				{ 'tolower(name)': { contains: searchString } },
				{ 'tolower(customer/code)': { contains: searchString } },
				{ 'tolower(customer/name)': { contains: searchString } },
			],
		});
	} else if (id) {
		context.filter!.and!.push({ id: { eq: id } });
	}

	if (notInIds && isArray(notInIds) && notInIds.length > 0) {
		context.filter!.and!.push({
			not: [{ id: { in: notInIds } }],
		});
	}

	const result = await ordersService.getOrders(buildQuery(context));
	return result.value;
}
